'use client';

import { useState, useEffect, useRef, Suspense, KeyboardEvent } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';
import { guidelinesLegalContent, type ContentBlock, type DocumentContent } from './content';

type TabType = 'community-guidelines' | 'terms' | 'privacy';
const TABS: TabType[] = ['community-guidelines', 'terms', 'privacy'];
const DEFAULT_TAB: TabType = 'community-guidelines';

function isTabType(value: string | null): value is TabType {
  return value !== null && (TABS as readonly string[]).includes(value);
}

function ContentBlocks({ blocks }: { blocks: readonly ContentBlock[] }) {
  return (
    <>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'heading':
            if (block.level === 2) {
              return (
                <h2
                  key={idx}
                  className="text-2xl font-semibold text-primary mt-8 mb-3 font-[family-name:var(--font-abhaya-libre)]"
                >
                  {block.text}
                </h2>
              );
            }
            return (
              <h3
                key={idx}
                className="text-lg font-semibold text-primary mt-5 mb-2"
              >
                {block.text}
              </h3>
            );
          case 'paragraph':
            return (
              <p key={idx} className="text-base text-primary leading-relaxed my-3">
                {typeof block.text === 'string'
                  ? block.text
                  : block.text.map((seg, i) =>
                      typeof seg === 'string' ? seg : <strong key={i}>{seg.bold}</strong>,
                    )}
              </p>
            );
          case 'list':
            return (
              <ul key={idx} className="list-disc pl-6 my-3 space-y-1.5">
                {block.items.map((item, i) => (
                  <li key={i} className="text-base text-primary leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            );
          case 'divider':
            return <hr key={idx} className="my-6 border-border" />;
        }
      })}
    </>
  );
}

function Document({ doc, id, labelledBy }: { doc: DocumentContent; id: string; labelledBy: string }) {
  const { t } = useLanguage();
  return (
    <div
      id={id}
      role="tabpanel"
      aria-labelledby={labelledBy}
      tabIndex={0}
      className="bg-white border border-border rounded-xl p-4 md:p-10 focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <h2 className="text-3xl font-semibold text-primary font-[family-name:var(--font-abhaya-libre)]">
        {doc.title}
      </h2>
      <p className="text-sm text-foreground-muted mt-1 mb-6">
        {t.guidelinesLegal.lastUpdated}: {doc.lastUpdated}
      </p>
      <ContentBlocks blocks={doc.blocks} />
    </div>
  );
}

function GuidelinesLegalPageInner() {
  const { t, language } = useLanguage();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabType>(
    isTabType(queryTab) ? queryTab : DEFAULT_TAB,
  );
  const tabRefs = useRef<Record<TabType, HTMLButtonElement | null>>({
    'community-guidelines': null,
    terms: null,
    privacy: null,
  });

  // Pull state from the URL whenever the query param changes (back/forward or
  // pasted link). Reset to the default tab when the param is missing or invalid.
  useEffect(() => {
    setActiveTab(isTabType(queryTab) ? queryTab : DEFAULT_TAB);
  }, [queryTab]);

  const selectTab = (tab: TabType, opts: { focus?: boolean } = {}) => {
    setActiveTab(tab);
    router.replace(`${pathname}?tab=${tab}`, { scroll: false });
    if (opts.focus) {
      tabRefs.current[tab]?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TABS.indexOf(activeTab);
    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % TABS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    selectTab(TABS[nextIndex], { focus: true });
  };

  const content = guidelinesLegalContent[language];

  const tabs: { id: TabType; label: string; panelId: string; tabId: string }[] = [
    {
      id: 'community-guidelines',
      label: t.guidelinesLegal.tabs.communityGuidelines,
      panelId: 'guidelines-legal-panel-community-guidelines',
      tabId: 'guidelines-legal-tab-community-guidelines',
    },
    {
      id: 'terms',
      label: t.guidelinesLegal.tabs.terms,
      panelId: 'guidelines-legal-panel-terms',
      tabId: 'guidelines-legal-tab-terms',
    },
    {
      id: 'privacy',
      label: t.guidelinesLegal.tabs.privacy,
      panelId: 'guidelines-legal-panel-privacy',
      tabId: 'guidelines-legal-tab-privacy',
    },
  ];

  const docFor = (id: TabType): DocumentContent => {
    switch (id) {
      case 'community-guidelines':
        return content.communityGuidelines;
      case 'terms':
        return content.terms;
      case 'privacy':
        return content.privacy;
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-6">
      <h1
        className="text-[48px] leading-tight text-primary"
        style={{ fontFamily: 'var(--font-abhaya-libre)' }}
      >
        {t.guidelinesLegal.title}
      </h1>

      <div
        className="flex gap-2 py-3 border-b border-border overflow-x-auto"
        role="tablist"
        aria-label={t.guidelinesLegal.title}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={tab.tabId}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={tab.panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => selectTab(tab.id)}
              onKeyDown={handleKeyDown}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap border ${
                isActive
                  ? 'bg-tab-active-bg border-filter-bg text-black'
                  : 'bg-white border-border text-foreground-muted hover:border-primary hover:text-foreground'
              }`}
              data-testid={tab.tabId}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tabs.map((tab) =>
          activeTab === tab.id ? (
            <Document
              key={tab.id}
              doc={docFor(tab.id)}
              id={tab.panelId}
              labelledBy={tab.tabId}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

export default function GuidelinesLegalPage() {
  return (
    <Suspense fallback={<div className="p-3 md:p-6" />}>
      <GuidelinesLegalPageInner />
    </Suspense>
  );
}
